import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  GraduationCap, LayoutDashboard, Building2, FilePlus2, Clock, CalendarCheck,
  FileText, Star, UserCog, School, DollarSign, MapPin, Users, ShieldCheck,
  ClipboardList, ArrowRight, CheckCircle2, Heart, Lock,
} from 'lucide-react';

const HERO = '/internship-portal-hero.jpg';

const InternshipPortal = () => {
  useEffect(() => {
    document.title = 'Manage Internship Portal | Field Education Tools | I Need A Social Worker';
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); }
    m.content = 'The first internship management portal built for social work field education — find placements, track required hours, submit weekly logs, manage learning agreements, evaluations, and documents in one private, secure place.';
  }, []);

  const features = [
    { icon: ClipboardList, label: 'Internship Applications' },
    { icon: Clock, label: 'Required Hours Tracking' },
    { icon: CalendarCheck, label: 'Weekly Hour Logs' },
    { icon: FileText, label: 'Learning Agreements' },
    { icon: Star, label: 'Evaluations' },
    { icon: UserCog, label: 'Field Instructor Info' },
    { icon: School, label: 'School Affiliation' },
    { icon: DollarSign, label: 'Paid or Unpaid Status' },
    { icon: MapPin, label: 'Remote or In-Person' },
    { icon: Users, label: 'Population & Practice Setting' },
  ];

  const canDo = [
    'Find and apply for social-work internships',
    'Manage internship documents securely',
    'Track your required internship hours',
    'Submit and organize weekly hour logs',
    'Keep learning agreements & school documents',
    'Record field-instructor information privately',
    'Track applications from interest to placement',
    'Watch your progress toward completion',
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative bg-primary pt-24 pb-16 overflow-hidden" data-testid="portal-hero">
        <div className="absolute inset-y-0 right-0 w-1/2 hidden lg:block">
          <img src={HERO} alt="Internship management dashboard on laptop and phone" className="w-full h-full object-cover object-left opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl text-white">
            <p className="inline-flex items-center gap-2 text-secondary font-semibold tracking-widest uppercase text-xs mb-4 bg-secondary/15 px-3 py-1.5 rounded-full">
              <Star className="w-3.5 h-3.5" /> First-ever · Built for Social Work
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
              Manage <span className="text-secondary">Internship</span> Portal
            </h1>
            <p className="mt-3 text-xl text-white/90 font-medium">Everything you need. All in one place.</p>
            <p className="mt-4 text-white/80 max-w-xl">
              Powerful, private tools to help social work students manage the full field-education journey — from finding a placement to tracking hours, logs, documents, and progress toward completion.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
              <Link to="/internships"><Button className="w-full h-12 bg-secondary hover:bg-secondary-hover text-white font-semibold" data-testid="portal-btn-find"><GraduationCap className="w-4 h-4 mr-2" /> Find an Internship</Button></Link>
              <Link to="/my-internship-dashboard"><Button className="w-full h-12 bg-white text-primary hover:bg-gray-100 font-semibold" data-testid="portal-btn-intern"><LayoutDashboard className="w-4 h-4 mr-2" /> Intern Portal</Button></Link>
              <Link to="/post-an-internship"><Button variant="outline" className="w-full h-12 border-white text-white hover:bg-white hover:text-primary font-semibold" data-testid="portal-btn-agency"><Building2 className="w-4 h-4 mr-2" /> Agency Portal</Button></Link>
              <Link to="/post-an-internship"><Button variant="outline" className="w-full h-12 border-secondary text-secondary hover:bg-secondary hover:text-white font-semibold" data-testid="portal-btn-post"><FilePlus2 className="w-4 h-4 mr-2" /> Post an Internship</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Simplify band */}
      <section className="bg-primary/95 border-y-4 border-secondary py-4">
        <p className="text-center text-white font-semibold tracking-widest uppercase text-sm">
          Simplify · Organize · Track · <span className="text-secondary">Succeed</span>
        </p>
      </section>

      {/* What you can do */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-3xl font-bold text-primary mb-4">Manage your entire internship in one place</h2>
            <p className="text-gray-600 mb-6">Field education is a central part of social-work training — and keeping it organized shouldn't be hard. The Manage Internship Portal brings it all together.</p>
            <ul className="grid sm:grid-cols-2 gap-3">
              {canDo.map((c) => (
                <li key={c} className="flex items-start gap-2 text-gray-700" data-testid="portal-can-do">
                  <CheckCircle2 className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" /> <span className="text-sm">{c}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/my-internship-dashboard"><Button className="bg-primary hover:bg-primary/90 text-white" data-testid="portal-open-dashboard">Open My Internship Dashboard <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <Link to="/track-my-hours"><Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white" data-testid="portal-track-hours"><Clock className="w-4 h-4 mr-2" /> Track My Hours</Button></Link>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {features.map((f) => (
              <div key={f.label} className="bg-white rounded-2xl border p-5 text-center hover:shadow-md hover:border-secondary/40 transition-all">
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <f.icon className="w-5 h-5 text-primary" />
                </div>
                <p className="text-xs font-semibold text-gray-700 leading-snug">{f.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy statement */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-primary/5 border border-primary/15 rounded-2xl p-8 flex items-start gap-4" data-testid="portal-privacy">
            <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <Lock className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <h3 className="font-serif text-xl font-bold text-primary mb-2">Your records are private</h3>
              <p className="text-gray-700">
                Your internship records are private. They may be viewed only by you and authorized INeedASocialWorker.com administrators. Schools, agencies, field instructors, employers, and other members cannot access your private internship records.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom band */}
      <section className="py-14 bg-primary">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6 text-white">
          <div className="flex items-center gap-3">
            <Heart className="w-9 h-9 text-secondary" />
            <div>
              <p className="font-serif text-xl font-bold">Built for social work. Designed for success.</p>
              <p className="text-white/80 text-sm">Stronger connections. Better outcomes.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/internships"><Button className="bg-secondary hover:bg-secondary-hover text-white">Find an Internship</Button></Link>
            <Link to="/internship-portal/membership"><Button className="bg-white text-primary hover:bg-gray-100" data-testid="portal-membership-cta">View Membership Plans</Button></Link>
            <Link to="/my-internship-dashboard"><Button variant="outline" className="border-white text-white hover:bg-white hover:text-primary">Go to My Dashboard</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InternshipPortal;
